import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useAccess } from "../../auth/useAccess";
import { logout as logoutRequest } from "../../lib/auth";
import { queryClient } from "../../lib/queryClient";
import { visibleNavGroups } from "./nav";
import { BrandMark } from "../../branding/Brand";
import { useBranding } from "../../branding/brandingStore";
import { Icon } from "../ui/Icon";
import { CopilotLogo } from "../ui/CopilotLogo";
import { NAV_CHAT_SLOT_ID } from "./navSlot";

interface SidebarProps {
  open: boolean;
  onNavigate: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onOpenAccount?: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

/**
 * App navigation, drawn as the canvas draws it: a 288px light-chrome column
 * holding the tool and administration groups, then — this is the design's
 * default "unified nav" workspace layout — the Copilot chat list, and finally
 * the account button.
 *
 * The chat list is not a child of this component: it belongs to the Copilot
 * page, which owns the conversation state and the server bindings. It is
 * portalled into `NAV_CHAT_SLOT_ID` below, so the visual arrangement is the
 * canvas's while the data flow stays where it was.
 */
export function Sidebar({
  open,
  onNavigate,
  collapsed = false,
  onToggleCollapse,
  onOpenAccount,
}: SidebarProps): JSX.Element {
  const { user, isLoading } = useAuth();
  const { atLeast, can } = useAccess();
  const location = useLocation();
  const branding = useBranding();

  // Module-aware nav: an entry for a module the principal cannot see is not
  // rendered at all. The list and the filter live in ./nav so the landing route
  // cannot disagree with the sidebar about what is reachable. UX only — the
  // backend enforces authorization on every request.
  const navGroups = visibleNavGroups({ atLeast, can });
  const toolGroups = navGroups.filter((group) => group.label !== "Administration");
  const administrationGroups = navGroups.filter((group) => group.label === "Administration");

  async function onLogout(): Promise<void> {
    await logoutRequest();
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    window.location.assign("/login");
  }

  return (
    <aside className={`sidebar${open ? " open" : ""}${collapsed ? " collapsed" : ""}`}>
      <div className="sidebar-brand">
        <BrandMark size={60} className="sidebar-brand-mark" />
        {!collapsed && (
          <>
            <span className="sidebar-wordmark">{branding.productName}</span>
            {onToggleCollapse && (
              <button
                type="button"
                className="sidebar-collapse-button"
                onClick={onToggleCollapse}
                aria-label="Collapse navigation"
                title="Collapse navigation"
              >
                <Icon name="chevronLeft" size={15} />
              </button>
            )}
          </>
        )}
      </div>

      {collapsed && onToggleCollapse && (
        <button
          type="button"
          className="sidebar-expand-button"
          onClick={onToggleCollapse}
          aria-label="Expand navigation"
          title="Expand navigation"
        >
          <Icon name="chevronRight" size={15} />
        </button>
      )}

      <nav className="sidebar-nav">
        {/* The Copilot page supplies the same product entry as the chat-list
            heading, so it is rendered here only on the administration pages.
            That keeps the way back visible without duplicating it above the
            conversation list. */}
        {!location.pathname.startsWith("/copilot") && toolGroups.map((group, index) => (
          <div key={group.label ?? `group-${index}`} className="sidebar-nav-group">
            {group.items.map((item) => (
              <div className="nav-item-wrap" key={item.label}>
                <NavLink
                  to={item.to}
                  end
                  className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                  onClick={onNavigate}
                  aria-label={collapsed ? item.label : undefined}
                  title={collapsed ? undefined : item.label}
                  data-tooltip={item.label}
                >
                  {item.to === "/copilot" ? (
                    <CopilotLogo size={18} />
                  ) : (
                    <Icon name={item.icon} size={17} />
                  )}
                  {!collapsed && <span className="nav-item-label">{item.label}</span>}
                </NavLink>
              </div>
            ))}
          </div>
        ))}

        {/* Layout C: the chats live inside the app navigation. Filled by the
            Copilot page while it is mounted; an empty div everywhere else,
            exactly as the canvas shows no chat section on Settings. */}
        {!collapsed && <div id={NAV_CHAT_SLOT_ID} className="sidebar-chat-slot" />}

        {administrationGroups.length > 0 && (
          <div className="sidebar-management">
            {administrationGroups.map((group, index) => (
              <div key={group.label ?? `administration-${index}`} className="sidebar-nav-group">
                {group.label && !collapsed && (
                  <div className="sidebar-section-label">{group.label}</div>
                )}
                {group.items.map((item) => (
                  <div className="nav-item-wrap" key={item.label}>
                    <NavLink
                      to={item.to}
                      end
                      className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
                      onClick={onNavigate}
                      aria-label={collapsed ? item.label : undefined}
                      title={collapsed ? undefined : item.label}
                      data-tooltip={item.label}
                    >
                      <Icon name={item.icon} size={17} />
                      {!collapsed && <span className="nav-item-label">{item.label}</span>}
                    </NavLink>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </nav>

      <div className="sidebar-footer">
        {user ? (
          <div className="sidebar-user-card">
            <button
              type="button"
              className="sidebar-user-account"
              aria-label={`Open account details for ${user.displayName}`}
              data-tooltip={user.displayName}
              onClick={onOpenAccount}
            >
              <span className="sidebar-user-avatar" data-on-brand>{initials(user.displayName)}</span>
              {!collapsed && (
                <>
                  <span className="sidebar-user-copy">
                    <strong>{user.displayName}</strong>
                    <span>{user.email}</span>
                  </span>
                </>
              )}
            </button>
            <button
              type="button"
              className="sidebar-logout-button"
              aria-label="Log out"
              title="Log out"
              data-tooltip="Log out"
              onClick={() => void onLogout()}
              data-testid="sidebar-logout"
            >
              <Icon name="logout" size={17} />
            </button>
          </div>
        ) : (
          <div className="sidebar-user-loading">{isLoading ? "Loading…" : "Not signed in"}</div>
        )}
      </div>
    </aside>
  );
}
